import { createContext } from "preact";
import { useContext, useState, useRef, useCallback, useEffect } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { useUserInteraction } from "./UserInteractionProvider";
import { useAppMessage, useDaily } from "@daily-co/daily-react";

export const ACTIVE_STATE = "active";
export const MINIMIZED_STATE = "minimized";
export const HIDDEN_STATE = "hidden";

export type WidgetState = typeof ACTIVE_STATE | typeof MINIMIZED_STATE | typeof HIDDEN_STATE;

interface WidgetContextType {
  widgetState: WidgetState;
  setWidgetState: (state: WidgetState) => void;
  isAgentVideoReady: boolean;
  setIsAgentVideoReady: (ready: boolean) => void;
  roomUrl: string | null;
  setRoomUrl: (url: string | null) => void;
  isTransitioning: boolean;
  isAboutToTransition: boolean;
  hasJoinedRoom: boolean;
  setHasJoinedRoom: (joined: boolean) => void;
  resetDailyState: () => void;
  showMinimizeButton: boolean;
  setShowMinimizeButton: (show: boolean) => void;
}

const WidgetContext = createContext<WidgetContextType | undefined>(undefined);
const ROOM_URL_TIMEOUT = 30000; // 30 seconds

interface WidgetProviderProps {
  children: ComponentChildren;
  showMinimizeButton?: boolean;
  onSetShowMinimizeButton?: (setter: (show: boolean) => void) => void;
}

export function WidgetProvider({ children, showMinimizeButton: initialShowMinimizeButton = true, onSetShowMinimizeButton }: WidgetProviderProps) {
  const { setHasUserInteracted } = useUserInteraction();
  const sendAppMessage = useAppMessage();
  const callObject = useDaily();
  const [widgetState, setWidgetStateInternal] = useState<WidgetState>(MINIMIZED_STATE);
  const [isAgentVideoReady, setIsAgentVideoReady] = useState<boolean>(false);
  const [isAboutToTransition, setIsAboutToTransition] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [hasJoinedRoom, setHasJoinedRoom] = useState<boolean>(false);
  const [showMinimizeButton, setShowMinimizeButton] = useState<boolean>(initialShowMinimizeButton);
  const transitionTimeoutRef = useRef<number | null>(null);
  const transitionEndTimeoutRef = useRef<number | null>(null);
  const roomUrlTimeoutRef = useRef<number | null>(null);

  // Expose setShowMinimizeButton to parent component
  useEffect(() => {
    if (onSetShowMinimizeButton) {
      onSetShowMinimizeButton(setShowMinimizeButton);
    }
  }, [onSetShowMinimizeButton]);

  const resetDailyState = useCallback(() => {
    if (callObject) {
      try {
        const meetingState = callObject.meetingState();
        if (meetingState === "joined-meeting" || meetingState === "joining-meeting") {
          console.log("Sending force-end event to Daily");
          sendAppMessage({ event: "force-end" }, "*");
        }
      } catch (error) {
        // If there is an error, we're not in a meeting, so skip sendAppMessage
      }
    }
    setIsAgentVideoReady(false);
    setHasUserInteracted(false);
    setHasJoinedRoom(false);
    setRoomUrl(null);
  }, [callObject]);

  const setWidgetState = (newState: WidgetState) => {
    // Clear any existing timeouts
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current);
    }
    if (transitionEndTimeoutRef.current !== null) {
      clearTimeout(transitionEndTimeoutRef.current);
    }

    // Handle roomUrl timeout
    // Start timeout timer if going from active to minimized
    // Clear timer if going back to active
    if (newState === ACTIVE_STATE) {
      // Clear the roomUrl timeout if transitioning back to active
      if (roomUrlTimeoutRef.current !== null) {
        clearTimeout(roomUrlTimeoutRef.current);
        roomUrlTimeoutRef.current = null;
      }
    } else if (widgetState === ACTIVE_STATE && newState === MINIMIZED_STATE) {
      // Start timeout timer when going from active to minimized
      roomUrlTimeoutRef.current = window.setTimeout(() => {
        resetDailyState();
        roomUrlTimeoutRef.current = null;
      }, ROOM_URL_TIMEOUT);
    }

    // When transaitioning to or from "hidden" state, we need to render both components for a smooth transition
    if (newState === HIDDEN_STATE || widgetState === HIDDEN_STATE) {
      setIsAboutToTransition(true);

      // Actually update widgetState after 1ms to initially render components in their starting locations
      transitionTimeoutRef.current = window.setTimeout(() => {
        setWidgetStateInternal(newState);
        setIsAboutToTransition(false);
        setIsTransitioning(true);
        transitionTimeoutRef.current = null;

        // Set isTransitioning to false after 300ms to allow for animations
        transitionEndTimeoutRef.current = window.setTimeout(() => {
          setIsTransitioning(false);
          transitionEndTimeoutRef.current = null;
        }, 300);
      }, 1);
    } else {
      setWidgetStateInternal(newState);
      setIsAboutToTransition(false);
      setIsTransitioning(false);
    }
  };

  return (
    <WidgetContext.Provider
      value={{ widgetState, setWidgetState, isAgentVideoReady, setIsAgentVideoReady, roomUrl, setRoomUrl, isTransitioning, isAboutToTransition, hasJoinedRoom, setHasJoinedRoom, resetDailyState, showMinimizeButton, setShowMinimizeButton }}
    >
      {children}
    </WidgetContext.Provider>
  );
}

export function useWidget(): WidgetContextType {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error("useWidget must be used within a WidgetProvider");
  }
  return context;
}
